/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Text,
  Box,
  IntrinsicImage,
  Link,
  SanityRichText,
  Button,
} from '@tishman/components';
import { H } from '@hzdg/sectioning';
import React, { Fragment } from 'react';

import // LearnMoreLinkValidPages,
// useBuyTicketsLink,
// useI18n,
// useLearnMoreLink,
'../../../utils';
import { useLocalizedLink } from '../../../utils/use-localized-link';

import type { ThemeUIStyleObject } from 'theme-ui';
// import useIsMobile from '../../../breakpoints/useIsMobile';

interface BuyButtonProps {
  caption: string;
  url: string;
  price: string;
  sx?: ThemeUIStyleObject;
  className?: string;
}
const BuyButton = ({ url, price, className, caption, sx }: BuyButtonProps) => {
  const getLocalizedLink = useLocalizedLink();

  // Generate a testId by replacing slashes with hyphens and removing leading/trailing hyphens
  // This is used to create a unique, URL-based identifier for testing purposes
  // This is the most stable way to create a testId
  const testId = `${url
    .replace(/\//g, '-')
    .replace(/^-/, '')
    .replace(/-$/, '')}`;

  return url ? (
    <Link
      className={className}
      data-testid={testId}
      href={url.includes('@') ? url : getLocalizedLink(url)}
      rel="noreferrer"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 3,
        mt: 3,
        mb: '0px',
        ...sx,
        variant: 'buttons.inverted',
      }}
      useGatsbyLink={false}
    >
      <Text as="span" color="inherit" mx={2} variant="smallP">
        {caption}
      </Text>
      <Text as="span" mx={2} variant="ticketPriceSmall">
        {price}
      </Text>
    </Link>
  ) : (
    <Button disabled mt="18px" py="18px">
      {caption}
    </Button>
  );
};

export interface CardDetailProps {
  buttonPosition?: 'bottom' | 'aboveDescription';
  category: Queries.SanityTicketCategory;
  ticket: Queries.Maybe<Queries.SanityTicket>;
}

// const RenderLearnMoreLink = ({
//   ticket,
//   category,
// }: Pick<CardDetailProps, 'ticket' | 'category'>) => {
//   const hasSecondaryUrl = !!ticket.secondaryUrl?.url;
//   const otherHasSecondaryUrl = category.tickets.some((t) => {
//     return t.id !== ticket.id && !!t.secondaryUrl?.url;
//   });

//   const learnMoreLink = useLearnMoreLink(
//     ticket,
//     LearnMoreLinkValidPages.BuyTickets,
//   );

//   const { t } = useI18n();

//   if (!learnMoreLink && !hasSecondaryUrl && !otherHasSecondaryUrl) {
//     return <Fragment />;
//   }

//   if (learnMoreLink || hasSecondaryUrl) {
//     return (
//       <Link
//         href={learnMoreLink ?? ticket.secondaryUrl!.url ?? ''}
//         sx={{
//           fontWeight: 'regular',
//           display: 'flex',
//           justifyContent: 'center',
//           bg: 'transparent',
//           border: '1px solid',
//           color: 'text',
//           p: 3,
//           mt: 3,
//           mb: 0,
//         }}
//         variant="button"
//       >
//         {learnMoreLink
//           ? t('learn_more', { namespace: 'tor' })
//           : ticket.secondaryUrl!.caption}
//       </Link>
//     );
//   }

//   if (otherHasSecondaryUrl) {
//     return (
//       <Link
//         href="#"
//         sx={{
//           fontWeight: 'regular',
//           display: ['none', 'flex'],
//           justifyContent: 'center',
//           bg: 'transparent',
//           border: '1px solid',
//           color: 'text',
//           p: 3,
//           mt: 3,
//           mb: 0,
//           visibility: 'hidden',
//         }}
//         variant="button"
//       >
//         &nbsp;
//       </Link>
//     );
//   }

//   return <Fragment />;
// };

export function CardDetail({
  buttonPosition = 'bottom',
  category,
  ticket,
}: CardDetailProps): React.JSX.Element {
  // const isMobile = useIsMobile();
  // const hasTertiaryUrl = !!ticket.tertiaryUrl?.url;
  // const otherHasTertiaryUrl = category.tickets.some((t) => {
  //   return t.id !== ticket.id && !!t.tertiaryUrl?.url;
  // });
  const getLocalizedLink = useLocalizedLink();

  const getLocalizedUrl = () => {
    const defaultUrl = ticket?.url?.url ?? '';

    if (ticket?.url?.caption?.toLowerCase().includes('email')) {
      const emailPattern = /\S+@\S+\.\S+/;
      const emailMatch = ticket.url.caption.match(emailPattern);
      if (emailMatch) {
        return `mailto:${emailMatch[0]}`;
      }
    }

    const localizedUrl = getLocalizedLink(defaultUrl);

    return localizedUrl;
  };

  return ticket ? (
    <Fragment>
      <Box
        sx={{
          flex: '2 2 auto',
        }}
      >
        {ticket?.image?.asset?.gatsbyImageData && (
          <IntrinsicImage
            alt={ticket.image.alt?.replace('N/A', '')}
            image={ticket?.image?.asset?.gatsbyImageData}
            ratio={[238 / 160, 316 / 212]}
          />
        )}
        <H
          sx={{
            variant: 'styles.h2',
            mt: [3],
            fontFamily: 'headingSecondary',
            fontSize: [4, 6],
            fontWeight: ['book', 'heading'],
          }}
        >
          {ticket.title}
        </H>
        {buttonPosition === 'aboveDescription' && (
          <BuyButton
            caption={ticket?.url?.caption || ''}
            price={ticket?.price || ''}
            url={getLocalizedUrl()}
          />
        )}
        {ticket.description && Array.isArray(ticket.description) && (
          <Text mt={3} sx={{ 'p:last-child': { mb: '0px' } }} variant="smallP">
            <SanityRichText blocks={ticket.description} />
          </Text>
        )}
      </Box>

      {buttonPosition === 'bottom' && (
        <BuyButton
          caption={ticket?.url?.caption || ''}
          price={ticket.price || ''}
          url={getLocalizedUrl()}
        />
      )}

      {/* <RenderLearnMoreLink category={category} ticket={ticket} /> */}

      {/* {hasTertiaryUrl ? (
        <Link
          href={ticket.tertiaryUrl.url}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
            mb: 0,
            mx: 'auto',
          }}
          variant="underline"
        >
          {ticket.tertiaryUrl.caption}
        </Link>
      ) : otherHasTertiaryUrl ? (
        <Link
          href="#"
          sx={{
            display: ['none', 'flex'],
            justifyContent: 'center',
            mt: 3,
            mb: 0,
            mx: 'auto',
            visibility: 'hidden',
          }}
          variant="underline"
        >
          &nbsp;
        </Link>
      ) : null} */}
    </Fragment>
  ) : (
    <Fragment />
  );
}
